import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";
import { fetchAllCrmContacts, fetchAllCrmDeals } from "@/lib/crmAggregate";
import { crmAccountMatchesQuery, crmTextMatchesQuery } from "@/lib/crmDisplay";

type LeadRow = {
  id: string;
  leadName?: string;
  companyName?: string | null;
  companyDomain?: string | null;
  stage?: string;
  status?: string;
  source?: string | null;
  contacts?: Array<{ fullName?: string | null; email?: string | null; linkedinUrl?: string | null }>;
};

type ContactRow = {
  id: string;
  accountId: string;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  title?: string | null;
  linkedinUrl?: string | null;
};

type AccountRow = {
  id: string;
  name?: string;
  domain?: string | null;
  status?: string | null;
};

type DealRow = {
  id: string;
  name?: string | null;
  accountId: string;
  stage?: string | null;
  status?: string | null;
  amount?: number | null;
};

export function CrmSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(query);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [deals, setDeals] = useState<DealRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const [leadsRes, accountsRes, contactsRows, dealsRows] = await Promise.all([
        apiGet<{ leads?: LeadRow[] }>("/api/crm/leads?limit=200"),
        apiGet<{ accounts?: AccountRow[] }>("/api/crm/accounts?limit=200"),
        fetchAllCrmContacts(200),
        fetchAllCrmDeals(200),
      ]);
      if (cancelled) return;
      if (!leadsRes.ok && !accountsRes.ok) {
        setError(leadsRes.error || accountsRes.error || "Could not load CRM records.");
      } else {
        setError(null);
      }
      setLeads(leadsRes.ok ? (leadsRes.data?.leads ?? []) : []);
      setContacts(contactsRows);
      setAccounts(accountsRes.ok ? (accountsRes.data?.accounts ?? []) : []);
      setDeals(dealsRows);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSearch = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (input.trim()) next.set("q", input.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  const includesQuery = (values: Array<string | null | undefined>) =>
    !query || values.some((value) => crmTextMatchesQuery(value, query));

  const matchedLeads = useMemo(
    () =>
      leads
        .filter((lead) =>
          includesQuery([
            lead.leadName,
            lead.companyName,
            lead.companyDomain,
            lead.stage,
            lead.status,
            lead.source,
            ...(lead.contacts || []).flatMap((c) => [c.fullName, c.email, c.linkedinUrl]),
          ]),
        )
        .slice(0, 12),
    [leads, query],
  );

  const matchedContacts = useMemo(
    () =>
      contacts
        .filter((contact) =>
          includesQuery([
            contact.fullName,
            contact.firstName,
            contact.lastName,
            contact.email,
            contact.title,
            contact.accountId,
            contact.linkedinUrl,
          ]),
        )
        .slice(0, 12),
    [contacts, query],
  );

  const matchedAccounts = useMemo(
    () => accounts.filter((account) => crmAccountMatchesQuery(account, query)).slice(0, 12),
    [accounts, query],
  );

  const matchedDeals = useMemo(
    () =>
      deals
        .filter((deal) =>
          includesQuery([deal.name, deal.accountId, deal.stage, deal.status, String(deal.amount ?? ""), deal.id]),
        )
        .slice(0, 12),
    [deals, query],
  );

  const totalMatches =
    matchedLeads.length + matchedContacts.length + matchedAccounts.length + matchedDeals.length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Global CRM search</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Search across leads, contacts, accounts, and deals from one place.
        </p>
      </div>

      <form onSubmit={onSearch} className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search CRM records"
          className="w-full max-w-xl rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20"
        />
        <button
          type="submit"
          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20 dark:hover:bg-white/10"
        >
          Search
        </button>
      </form>

      {loading ? <p className="text-sm text-slate-500">Loading CRM index…</p> : null}
      {error ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      {query && !loading ? (
        <div className="rounded-md border border-gray-200 bg-white px-4 py-3 text-sm dark:border-white/10 dark:bg-white/5">
          <span className="font-medium">{totalMatches}</span> matches for <span className="font-medium">{query}</span>
        </div>
      ) : null}

      <SearchSection title="Leads" listHref={`/crm/leads?q=${encodeURIComponent(query)}`}>
        {matchedLeads.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-500">No leads found.</p>
        ) : (
          <ul className="divide-y dark:divide-white/10">
            {matchedLeads.map((lead) => (
              <li key={lead.id} className="px-4 py-3 text-sm">
                <Link to={`/crm/leads/${encodeURIComponent(lead.id)}`} className="font-medium text-indigo-700 hover:underline dark:text-indigo-300">
                  {lead.leadName}
                </Link>
                <div className="text-xs text-gray-500">
                  {lead.companyName || "No company linked"} · {(lead.stage || "").replace(/_/g, " ")}
                </div>
              </li>
            ))}
          </ul>
        )}
      </SearchSection>

      <SearchSection title="Contacts" listHref={`/crm/contacts?q=${encodeURIComponent(query)}`}>
        {matchedContacts.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-500">No contacts found.</p>
        ) : (
          <ul className="divide-y dark:divide-white/10">
            {matchedContacts.map((contact) => (
              <li key={contact.id} className="px-4 py-3 text-sm">
                <Link
                  to={`/crm/contacts/${encodeURIComponent(contact.id)}?accountId=${encodeURIComponent(contact.accountId)}`}
                  className="font-medium text-indigo-700 hover:underline dark:text-indigo-300"
                >
                  {contact.fullName || "Unknown"}
                </Link>
                <div className="text-xs text-gray-500">
                  {contact.email || "No email"} · {contact.accountId}
                </div>
              </li>
            ))}
          </ul>
        )}
      </SearchSection>

      <SearchSection title="Accounts" listHref={`/crm/accounts?q=${encodeURIComponent(query)}`}>
        {matchedAccounts.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-500">No accounts found.</p>
        ) : (
          <ul className="divide-y dark:divide-white/10">
            {matchedAccounts.map((account) => (
              <li key={account.id} className="px-4 py-3 text-sm">
                <Link to={`/crm/accounts/${encodeURIComponent(account.id)}`} className="font-medium text-indigo-700 hover:underline dark:text-indigo-300">
                  {account.name}
                </Link>
                <div className="text-xs text-gray-500">
                  {account.domain || "No domain"} · {account.status}
                </div>
              </li>
            ))}
          </ul>
        )}
      </SearchSection>

      <SearchSection title="Deals" listHref={`/crm/deals?q=${encodeURIComponent(query)}`}>
        {matchedDeals.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-500">No deals found.</p>
        ) : (
          <ul className="divide-y dark:divide-white/10">
            {matchedDeals.map((deal) => (
              <li key={deal.id} className="px-4 py-3 text-sm">
                <Link
                  to={`/crm/deals/${encodeURIComponent(deal.id)}?accountId=${encodeURIComponent(deal.accountId)}`}
                  className="font-medium text-indigo-700 hover:underline dark:text-indigo-300"
                >
                  {deal.name}
                </Link>
                <div className="text-xs text-gray-500">
                  {deal.accountId} · {(deal.stage || "").replace(/_/g, " ")}
                </div>
              </li>
            ))}
          </ul>
        )}
      </SearchSection>
    </div>
  );
}

function SearchSection({
  title,
  listHref,
  children,
}: {
  title: string;
  listHref: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{title}</h2>
        <Link to={listHref} className="text-sm text-indigo-600 hover:underline dark:text-indigo-300">
          Open {title}
        </Link>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white dark:border-white/10 dark:bg-white/5">{children}</div>
    </section>
  );
}
